import React from 'react';
import { Crown } from 'lucide-react';
import Tooltip from 'components/ui/tooltip';
import { cn } from 'lib/utils';
import { useOpenUpgradeForLimit } from 'modules/entitlements';

interface UpgradeFieldBadgeProps {
  /** Feature key this field is gated by — drives the upgrade dialog payload. */
  featureKey: string;
  /** The active plan's numeric limit for this field. */
  limit: number;
  /** What the user is currently requesting (shown as used/limit in the dialog). */
  requested?: number;
  /** Explanation shown on hover (a plain string is also used as the aria-label). */
  tooltip: React.ReactNode;
  /** Optional short label rendered next to the crown (e.g. "Premium"). */
  label?: string;
  className?: string;
}

/** Clicking opens the app-wide upgrade dialog pre-filled for `featureKey`. UX only — the backend gates. */
export function UpgradeFieldBadge({
  featureKey,
  limit,
  requested,
  tooltip,
  label,
  className,
}: UpgradeFieldBadgeProps) {
  const openUpgrade = useOpenUpgradeForLimit();

  return (
    <Tooltip
      content={tooltip}
      side="top"
      className="w-72 max-w-[calc(100vw-2rem)] whitespace-normal px-3.5 py-2 text-center text-[13px] leading-relaxed"
    >
      <button
        type="button"
        onClick={() => openUpgrade(featureKey, limit, requested)}
        aria-label={typeof tooltip === 'string' ? tooltip : undefined}
        className={cn(
          'inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5',
          'text-[11px] font-semibold text-foreground/80 transition-colors',
          'hover:border-foreground/30 hover:text-foreground',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className,
        )}
      >
        <Crown className="size-3" aria-hidden />
        {label}
      </button>
    </Tooltip>
  );
}

export default UpgradeFieldBadge;
